<script>
    import { onMount } from 'svelte';
    import { timeParse, timeFormat, scaleLinear, scaleTime, min, max, extent, line, area, curveMonotoneX, timeYear } from 'd3';
    import YAxis from './components/yAxis.svelte';
    import { blue } from './utils/colors.js';

    // Fetched live from CNN's mirror of the USBR Lake Powell reservoir data,
    // rather than baked in at build time.
    const DATA_URL = 'https://ix.cnn.io/data/mead-powell-water-levels/49_919.json';

    let { width = 600 } = $props();

    let height = $state(0);

    const startDate = timeParse('%Y-%m-%d')('1980-01-01');

    let levels = $state(null);

    onMount(async () => {
        try {
            const response = await fetch(DATA_URL);
            if (!response.ok) throw new Error(`HTTP error. Status: ${response.status}`);
            levels = await response.json();
        } catch (error) {
            console.error('Error fetching water level data: ', error.message);
        }
    });

    const data = $derived(
        (levels?.data ?? []).map(d => {
            const datetime = timeParse('%Y-%m-%d')(d[0]);
            return {
                datetime,
                elevation: d[1],
            };
        }).filter(d => d.datetime >= startDate)
    );

    // Lake Mead "dead pool" — the elevation below which water can no longer
    // pass through Hoover Dam — used as the chart floor.
    const DEAD_POOL = 3370;

    const margin = { top: 14, left: 0, bottom: 28, right: 52 };
    const chartwidth = $derived(width - margin.left - margin.right);
    const chartheight = $derived(height - margin.top - margin.bottom);

    const dataMax = $derived(max(data, d => d.elevation));

    const yScale = $derived(scaleLinear().domain([DEAD_POOL, dataMax]).range([chartheight, 0]));
    const xScale = $derived(scaleTime().domain(extent(data, d => d.datetime)).range([0, chartwidth]));

    const yTicks = $derived([3370, 3400, 3500, 3600, 3700]);
    const xTicks = $derived(xScale.ticks(timeYear.every(10)));

    const fmtYear = timeFormat('%Y');
    const fmtDate = timeFormat('%b %-d, %Y');

    const lineGen = $derived(
        line()
            .x(d => xScale(d.datetime))
            .y(d => yScale(d.elevation))
            .curve(curveMonotoneX)
    );

    const path = $derived(lineGen(data));

    // Fill the space between the line and the dead-pool floor (the y-domain min).
    const areaGen = $derived(
        area()
            .x(d => xScale(d.datetime))
            .y0(yScale(DEAD_POOL))
            .y1(d => yScale(d.elevation))
            .curve(curveMonotoneX)
    );

    const areaPath = $derived(areaGen(data));

    const lastPoint = $derived(data[data.length - 1]);

    // The previous record low was set in July 2022. Pin the annotation to the
    // lowest reading within that year (rather than the all-time min) so it stays
    // on the 2022 record even if the lake drops to a new low in the future.
    const RECORD_YEAR = 2023;
    const recordLow = $derived.by(() => {
        const inYear = data.filter(d => d.datetime.getFullYear() === RECORD_YEAR);
        if (!inYear.length) return null;
        return inYear.reduce((lo, d) => (d.elevation < lo.elevation ? d : lo), inYear[0]);
    });

</script>

<div class="graphic-wrapper" bind:clientHeight={height}>
    {#if width && height && data.length}
        <svg {width} {height}>
            <g class="chart" transform="translate({margin.left}, {margin.top})">
                <YAxis {yScale} ticks={yTicks} {chartwidth} unit="ft" part="grid" />

                <g class="x-axis">
                    {#each xTicks as tick, i}
                        <line class="year-tick" 
                            x1={xScale(tick)} 
                            x2={xScale(tick)} 
                            y1={chartheight + 6}
                            y2={chartheight}
                            stroke-width=1
                        >
                        </line>
                        <text
                            class="year-label"
                            class:start={i === 0}
                            x={xScale(tick)}
                            y={chartheight + 18}
                        >{fmtYear(tick)}</text>
                    {/each}
                </g>

                <path class="elevation-area" d={areaPath} stroke="none"></path>
                <path class="elevation-line" d={path} fill="none"></path>

                <YAxis {yScale} ticks={yTicks} {chartwidth} unit="ft" part="labels" />

                {#if recordLow}
                    {@const rx = xScale(recordLow.datetime)}
                    {@const ry = yScale(recordLow.elevation)}
                    <g class="record-marker" transform="translate({rx}, {ry})">
                        <circle class="dot" r="12" fill="none"></circle>
                        <text class="record-title halo" x="-18" y="-6">Previous record low since 1980</text>
                        <text class="record-title" x="-18" y="-6">Previous record low since 1980</text>
                        <text class="record-value halo" x="-18" y="10">{recordLow.elevation.toLocaleString()} ft · {fmtYear(recordLow.datetime)}</text>
                        <text class="record-value" x="-18" y="10">{recordLow.elevation.toLocaleString()} ft · {fmtYear(recordLow.datetime)}</text>
                    </g>
                {/if}

                {#if lastPoint}
                    {@const cx = xScale(lastPoint.datetime)}
                    {@const cy = yScale(lastPoint.elevation)}
                    <g class="current-marker" transform="translate({cx}, {cy})">
                        <circle class="pulse" r="4"></circle>
                        <circle class="dot" r="4"></circle>
                        <text class="marker-date halo" x=0 y="24">{fmtDate(lastPoint.datetime)}</text>
                        <text class="marker-date" x=0 y="24">{fmtDate(lastPoint.datetime)}</text>
                        <text class="marker-value halo" x=0 y="40">{lastPoint.elevation.toLocaleString()} ft</text>
                        <text class="marker-value" x=0 y="40">{lastPoint.elevation.toLocaleString()} ft</text>
                    </g>
                {/if}
            </g>
        </svg>
    {/if}
</div>

<style lang="scss">
    @import '@/styles/variables';

    .graphic-wrapper {
        width: 100%;
        position: relative;
        height: 450px;
    }

    .elevation-area {
        fill: $blue;
        opacity: 0.1;
        pointer-events: none;
    }

    .elevation-line {
        stroke: $blue;
        stroke-width: 2.5;
        pointer-events: none;
    }

    .year-label {
        font-size: 11px;
        fill: #666;
        text-anchor: middle;
        pointer-events: none;

        &.start {
            text-anchor: start;
        }
    }

    .year-tick {
        stroke: $gray4;
    }

    .record-marker {
        pointer-events: none;

        .dot {
            fill: none;
            stroke: $blue;
            stroke-width: 1.5;
        }

        .record-title {
            font-size: 13px;
            font-weight: 700;
            fill: $blue;
            text-anchor: end;
        }

        .record-value {
            font-size: 13px;
            fill: $blue;
            text-anchor: end;
        }

        .halo {
            fill: #fff;
            stroke: #fff;
            stroke-width: 3.5;
            stroke-linejoin: round;
        }
    }

    .current-marker {
        pointer-events: none;

        .pulse {
            fill: $blue;
            transform-box: fill-box;
            transform-origin: center;
            animation: pulse 1.6s ease-out infinite;
        }

        .dot {
            fill: $blue;
        }

        .marker-date {
            font-size: 13px;
            font-weight: 700;
            fill: $blue;
            text-anchor: middle;
        }

        .marker-value {
            font-size: 13px;
            fill: $blue;
            text-anchor: middle;
        }

        .halo {
            fill: #fff;
            stroke: #fff;
            stroke-width: 3.5;
            stroke-linejoin: round;
        }
    }

    @keyframes pulse {
        0% {
            transform: scale(1);
            opacity: 0.55;
        }
        100% {
            transform: scale(2.6);
            opacity: 0;
        }
    }
</style>
